/**
 * Returns Management React Query Hooks
 * 
 * Provides React Query hooks for returns management operations including:
 * - Blind returns creation and management
 * - RMA (Return Merchandise Authorization) operations
 * - Returns forecasting
 * - Returns dashboards and analytics
 * 
 * All hooks use React Query for automatic caching, background refetching,
 * and optimistic updates with proper cache invalidation.
 * 
 * @module hooks/useReturns
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import returnsService from '../services/returnsService';
import api from '../services/api';

/**
 * Mutation hook to create a blind return
 * 
 * Automatically invalidates blind returns query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useCreateBlindReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.createBlindReturn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blind-returns'] });
    },
  });
};

/**
 * Mutation hook to upload photos for a blind return
 * 
 * @param {string} blindReturnId - Blind return ID
 * @param {Array} photos - Array of photo files
 * @param {string} photoType - Type of photos (e.g., 'product', 'damage')
 * @returns {Object} React Query mutation result
 */
export const useUploadBlindReturnPhotos = () => {
  return useMutation({
    mutationFn: ({ blindReturnId, photos, photoType }) => 
      returnsService.uploadBlindReturnPhotos(blindReturnId, photos, photoType),
  });
};

/**
 * Mutation hook to identify a blind return
 * 
 * @param {string} blindReturnId - Blind return ID
 * @param {string} method - Identification method
 * @returns {Object} React Query mutation result
 */
export const useIdentifyBlindReturn = () => {
  return useMutation({
    mutationFn: ({ blindReturnId, method }) => 
      returnsService.identifyBlindReturn(blindReturnId, method),
  });
};

/**
 * Mutation hook to link a blind return to an order
 * 
 * Automatically invalidates blind returns query on success
 * 
 * @param {string} blindReturnId - Blind return ID
 * @param {string} orderId - Order ID to link
 * @param {number} confidence - Confidence score
 * @param {string} notes - Additional notes
 * @returns {Object} React Query mutation result
 */
export const useLinkBlindReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ blindReturnId, orderId, confidence, notes }) => 
      returnsService.linkBlindReturnToOrder(blindReturnId, orderId, confidence, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blind-returns'] });
    },
  });
};

/**
 * Fetches blind returns with optional filtering
 * 
 * @param {Object} filters - Query parameters for filtering blind returns
 * @param {string} [filters.status] - Filter by status
 * @param {string} [filters.warehouse_id] - Filter by warehouse
 * @returns {Object} React Query result with blind returns data
 * @property {Array} data - Array of blind return records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useBlindReturns = (filters = {}) => {
  return useQuery({
    queryKey: ['blind-returns', filters],
    queryFn: () => returnsService.getBlindReturns(filters),
  });
};

export const useBlindReturnDetails = (blindReturnId) => {
  return useQuery({
    queryKey: ['blind-returns', blindReturnId],
    queryFn: () => returnsService.getBlindReturnDetails(blindReturnId),
    enabled: !!blindReturnId,
  });
};


export const useCreateRMA = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.createRMA,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rma'] });
    },
  });
};

export const useRMARequests = (filters = {}) => {
  return useQuery({
    queryKey: ['rma', filters],
    queryFn: () => returnsService.getRMARequests(filters),
  });
};

export const useRMADetails = (rmaNumber) => {
  return useQuery({
    queryKey: ['rma', rmaNumber],
    queryFn: () => returnsService.getRMADetails(rmaNumber),
    enabled: !!rmaNumber,
  });
};

export const useApproveRMA = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ rmaNumber, ...data }) => 
      returnsService.approveRMA(rmaNumber, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rma'] });
    },
  });
};

export const useRejectRMA = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ rmaNumber, reason }) => 
      returnsService.rejectRMA(rmaNumber, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rma'] });
    },
  });
};

export const useGenerateReturnLabel = () => {
  return useMutation({
    mutationFn: returnsService.generateReturnLabel,
  });
};

export const useReceiveRMAReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ rmaNumber, ...data }) => 
      returnsService.receiveRMAReturn(rmaNumber, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rma'] });
    },
  });
};


export const useReturns = (filters = {}) => {
  return useQuery({
    queryKey: ['returns', filters],
    queryFn: () => returnsService.getReturns(filters),
  });
};

export const useReturn = (returnId) => {
  return useQuery({
    queryKey: ['returns', returnId],
    queryFn: () => returnsService.getReturn(returnId),
    enabled: !!returnId,
  });
};

export const useCreateReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.createReturn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useUpdateReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ returnId, ...data }) => 
      returnsService.updateReturn(returnId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useDeleteReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.deleteReturn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useProcessReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.processReturn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useCompleteReturnWorkflow = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.completeReturnWorkflow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useQualityControlReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.qualityControlReturn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useRestockReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ returnId, ...restockData }) => 
      returnsService.restockReturn(returnId, restockData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useProcessRefund = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ returnId, ...refundData }) => 
      returnsService.processRefund(returnId, refundData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useBatchProcessReturns = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.batchProcessReturns,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};


export const useReturnReasons = (language = 'en') => {
  return useQuery({
    queryKey: ['return-reasons', language],
    queryFn: () => returnsService.getReturnReasons({ language, active_only: true }),
  });
};


export const useReturnsForecast = (dateRange, warehouseId) => {
  return useQuery({
    queryKey: ['returns-forecast', dateRange, warehouseId],
    queryFn: () => returnsService.getReturnsForecast(dateRange),
    enabled: !!dateRange?.from && !!dateRange?.to,
  });
};

export const useDailyForecast = (warehouseId, forecastDate) => {
  return useQuery({
    queryKey: ['daily-forecast', warehouseId, forecastDate],
    queryFn: () => returnsService.getDailyForecast(warehouseId, forecastDate),
    enabled: !!warehouseId && !!forecastDate,
  });
};

export const useGenerateForecast = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ warehouseId, forecastDate }) => 
      returnsService.generateForecast(warehouseId, forecastDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-forecast'] });
      queryClient.invalidateQueries({ queryKey: ['returns-forecast'] });
    },
  });
};

export const useDailyReturnsSchedule = (warehouseId, scheduleDate, shift = null) => {
  return useQuery({
    queryKey: ['returns-schedule', warehouseId, scheduleDate, shift],
    queryFn: () => returnsService.getDailyReturnsSchedule(warehouseId, scheduleDate, shift),
    enabled: !!warehouseId && !!scheduleDate,
  });
};

export const useGenerateWeeklySchedule = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ warehouseId, startDate, days }) => 
      returnsService.generateWeeklySchedule(warehouseId, startDate, days),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns-schedule'] });
    },
  });
};

export const useUpdateScheduleActuals = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ scheduleId, ...actualsData }) => 
      returnsService.updateScheduleActuals(scheduleId, actualsData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns-schedule'] });
    },
  });
};

export const useCreateDailySchedule = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.createDailySchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns-schedule'] });
    },
  });
};


export const useIncomingReturns = (filters = {}) => {
  return useQuery({
    queryKey: ['incoming-returns', filters],
    queryFn: () => returnsService.getIncomingReturns(filters),
  });
};

export const useSyncRMATracking = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.syncRMATracking,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incoming-returns'] });
    },
  });
};

export const useUpdateCarrierTracking = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ trackingNumber, ...carrierData }) => 
      returnsService.updateCarrierTracking(trackingNumber, carrierData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incoming-returns'] });
    },
  });
};

export const useDetectReturnsFromShipments = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: returnsService.detectReturnsFromShipments,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incoming-returns'] });
    },
  });
};

export const useMarkReturnReceived = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ trackingNumber, ...data }) => 
      returnsService.markReturnReceived(trackingNumber, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incoming-returns'] });
    },
  });
};


export const useOperatorReturnsDashboard = (operatorId, warehouseId, date) => {
  return useQuery({
    queryKey: ['returns-dashboard', 'operator', operatorId, warehouseId, date],
    queryFn: () => returnsService.getOperatorDashboard(operatorId, warehouseId, date),
    enabled: !!operatorId && !!warehouseId,
  });
};

export const useManagerReturnsDashboard = (warehouseId, dateFrom, dateTo) => {
  return useQuery({
    queryKey: ['returns-dashboard', 'manager', warehouseId, dateFrom, dateTo],
    queryFn: () => returnsService.getManagerDashboard(warehouseId, dateFrom, dateTo),
    enabled: !!warehouseId && !!dateFrom && !!dateTo,
  });
};


export const useReturnsMetrics = () => {
  return useQuery({
    queryKey: ['returns-metrics'],
    queryFn: returnsService.getMetrics,
  });
};

export const useReturnsAnalytics = (params = {}) => {
  return useQuery({
    queryKey: ['returns-analytics', params],
    queryFn: () => returnsService.getAnalytics(params),
  });
};

export const useReturnsFilters = () => {
  return useQuery({
    queryKey: ['returns-filters'],
    queryFn: returnsService.getFilters,
  });
};

export const useExportReturns = () => {
  return useMutation({
    mutationFn: returnsService.exportReturns,
  });
};

// =====================================================
// Returns Management Hooks (merged from useReturnsManagement)
// =====================================================

export const useCreateReturnRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/returns/requests', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useGetReturnRequests = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'requests', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/returns/requests', { params });
      return response.data;
    },
  });
};

export const useGetReturnRequestById = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'request', params],
    queryFn: async () => {
      const response = await api.get(`/api/v1/returns/requests/${params.id}`);
      return response.data;
    },
    enabled: !!params.id,
  });
};

export const useUpdateReturnRequestStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.put(`/api/v1/returns/requests/${data.id}/status`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useReceiveReturnMgmt = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'receive', params],
    queryFn: async () => {
      const response = await api.get(`/api/v1/returns/${params.id}/receive`);
      return response.data;
    },
    enabled: !!params.id,
  });
};

export const useAddReturnItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/api/v1/returns/${data.returnId}/items`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useGetReturnItems = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'items', params],
    queryFn: async () => {
      const response = await api.get(`/api/v1/returns/${params.returnId}/items`);
      return response.data;
    },
    enabled: !!params.returnId,
  });
};

export const useUpdateReturnItemInspection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.put(`/api/v1/returns/items/${data.itemId}/inspection`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

export const useCreateRefundMgmt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/returns/refunds', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'refunds'] });
    },
  });
};

export const useGetRefunds = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'refunds', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/returns/refunds', { params });
      return response.data;
    },
  });
};

export const useProcessRefundMgmt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.put(`/api/v1/returns/refunds/${data.id}/process`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'refunds'] });
    },
  });
};

export const useCreateExchange = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/returns/exchanges', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'exchanges'] });
    },
  });
};

export const useGetExchanges = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'exchanges', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/returns/exchanges', { params });
      return response.data;
    },
  });
};

export const useUpdateExchangeStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.put(`/api/v1/returns/exchanges/${data.id}/status`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'exchanges'] });
    },
  });
};

export const useGetReturnReasons = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'reasons', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/returns/reasons', { params });
      return response.data;
    },
  });
};

export const useCreateReturnReason = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/returns/reasons', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'reasons'] });
    },
  });
};

export const useGetReturnPolicies = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'policies', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/returns/policies', { params });
      return response.data;
    },
  });
};

export const useCreateReturnPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/returns/policies', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'policies'] });
    },
  });
};

export const useCreateInspection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/returns/inspections', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'inspections'] });
    },
  });
};

export const useGetInspections = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'inspections', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/returns/inspections', { params });
      return response.data;
    },
  });
};

export const useCreateRestockingRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/returns/restocking', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'restocking'] });
    },
  });
};

export const useGetRestockingRecords = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'restocking', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/returns/restocking', { params });
      return response.data;
    },
  });
};

export const useCreateStoreCredit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/returns/store-credits', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'store-credits'] });
    },
  });
};

export const useGetStoreCredits = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'store-credits', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/returns/store-credits', { params });
      return response.data;
    },
  });
};

export const useRecordAnalytics = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/returns/analytics', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'analytics'] });
    },
  });
};

export const useGetAnalytics = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'analyticsMgmt', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/returns/analytics', { params });
      return response.data;
    },
  });
};

export const useLogActivity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/returns/activity-log', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns', 'activity-log'] });
    },
  });
};

export const useGetActivityLog = (params = {}) => {
  return useQuery({
    queryKey: ['returns', 'activity-log', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/returns/activity-log', { params });
      return response.data;
    },
  });
};
